import { useEffect, useState } from "react";

import { cn } from "./lib/utils";

export type ModelBrand =
  | "openai"
  | "claude"
  | "deepseek"
  | "gemini"
  | "google"
  | "sora"
  | "doubao"
  | "bytedance"
  | "byteplus"
  | "volcengine"
  | "xai"
  | "grok"
  | "kimi"
  | "moonshot"
  | "zai"
  | "minimax"
  | "hailuo"
  | "kling"
  | "qwen"
  | "alibaba"
  | "happyhorse"
  | "wan"
  | "elevenlabs"
  | "midjourney"
  | "pixverse"
  | "vidu"
  | "fishaudio"
  | "heygen"
  | "worldlabs"
  | "azure"
  | "vertexai"
  | "aws"
  | "bedrock"
  | "generic";

const brandOrder: ReadonlyArray<readonly [ModelBrand, readonly string[]]> = [
  ["deepseek", ["deepseek"]],
  ["aws", ["aws", "amazon", "bedrock"]],
  ["hailuo", ["hailuo"]],
  ["kimi", ["kimi"]],
  ["zai", ["z-ai", "z.ai", "zhipu", "glm", "智谱"]],
  ["grok", ["grok"]],
  ["claude", ["claude", "anthropic"]],
  ["sora", ["sora"]],
  ["gemini", ["gemini", "veo", "imagen"]],
  ["vertexai", ["vertex"]],
  ["openai", ["openai", "gpt-", "/gpt", "chatgpt"]],
  ["doubao", ["doubao", "seedance", "seedream"]],
  ["byteplus", ["byteplus"]],
  ["volcengine", ["volcengine"]],
  ["happyhorse", ["happy-horse", "happyhorse"]],
  ["qwen", ["qwen"]],
  ["wan", ["wan-", "wanxiang", "wanx"]],
  ["minimax", ["minimax"]],
  ["kling", ["kling", "kuaishou"]],
  ["elevenlabs", ["elevenlabs", "eleven"]],
  ["midjourney", ["midjourney", "youchuan"]],
  ["pixverse", ["pixverse", "p55", "p60", "pc1"]],
  ["vidu", ["vidu", "vq3"]],
  ["fishaudio", ["fish-audio", "fishaudio"]],
  ["heygen", ["heygen"]],
  ["worldlabs", ["worldlabs", "marble"]],
  ["azure", ["azure"]],
  ["moonshot", ["moonshot"]],
  ["xai", ["xai"]],
  ["bytedance", ["bytedance", "seed-"]],
  ["alibaba", ["alibaba", "aliyun", "tongyi"]],
  ["google", ["google"]],
];

export const MODEL_ICONS_BASE_URL = "https://upload.nexra-ai.com/model-icons";

export const brandAssets: Record<Exclude<ModelBrand, "generic">, string> = {
  deepseek: `${MODEL_ICONS_BASE_URL}/deepseek-color.svg`,
  openai: `${MODEL_ICONS_BASE_URL}/openai.svg`,
  claude: `${MODEL_ICONS_BASE_URL}/claude-color.svg`,
  gemini: `${MODEL_ICONS_BASE_URL}/gemini-color.svg`,
  google: `${MODEL_ICONS_BASE_URL}/google-color.svg`,
  sora: `${MODEL_ICONS_BASE_URL}/sora-color.svg`,
  doubao: `${MODEL_ICONS_BASE_URL}/doubao-color.svg`,
  bytedance: `${MODEL_ICONS_BASE_URL}/bytedance-color.svg`,
  byteplus: `${MODEL_ICONS_BASE_URL}/byteplus.png`,
  volcengine: `${MODEL_ICONS_BASE_URL}/volcengine-color.svg`,
  xai: `${MODEL_ICONS_BASE_URL}/xai.svg`,
  grok: `${MODEL_ICONS_BASE_URL}/grok.svg`,
  kimi: `${MODEL_ICONS_BASE_URL}/kimi.svg`,
  moonshot: `${MODEL_ICONS_BASE_URL}/moonshot.svg`,
  zai: `${MODEL_ICONS_BASE_URL}/zai.svg`,
  minimax: `${MODEL_ICONS_BASE_URL}/minimax-color.svg`,
  hailuo: `${MODEL_ICONS_BASE_URL}/hailuo-color.svg`,
  kling: `${MODEL_ICONS_BASE_URL}/kling-color.svg`,
  qwen: `${MODEL_ICONS_BASE_URL}/qwen-color.svg`,
  alibaba: `${MODEL_ICONS_BASE_URL}/alibaba-color.svg`,
  happyhorse: `${MODEL_ICONS_BASE_URL}/happyhorse.svg`,
  wan: `${MODEL_ICONS_BASE_URL}/wan.png`,
  elevenlabs: `${MODEL_ICONS_BASE_URL}/elevenlabs.svg`,
  midjourney: `${MODEL_ICONS_BASE_URL}/midjourney.svg`,
  pixverse: `${MODEL_ICONS_BASE_URL}/pixverse-color.svg`,
  vidu: `${MODEL_ICONS_BASE_URL}/vidu-color.svg`,
  fishaudio: `${MODEL_ICONS_BASE_URL}/fishaudio.svg`,
  heygen: `${MODEL_ICONS_BASE_URL}/heygen.svg`,
  worldlabs: `${MODEL_ICONS_BASE_URL}/worldlabs.png`,
  azure: `${MODEL_ICONS_BASE_URL}/azure-color.svg`,
  vertexai: `${MODEL_ICONS_BASE_URL}/vertexai-color.svg`,
  aws: `${MODEL_ICONS_BASE_URL}/aws-color.svg`,
  bedrock: `${MODEL_ICONS_BASE_URL}/aws-color.svg`,
};

const sizeClass = {
  sm: "size-5 rounded-[5px]",
  md: "size-7 rounded-md",
  lg: "size-9 rounded-lg",
  xl: "size-11 rounded-xl",
} as const;

// Brand logos are code-owned catalog assets on the model-icons origin; they
// almost never change, so they are fetched once and then reused from a
// persistent cache instead of re-requesting on every visit to the admin
// channel/model pages.
const ICON_CACHE_STORAGE_KEY = "nexra.model-icons.v1";
const ICON_CACHE_ORIGIN = new URL(MODEL_ICONS_BASE_URL).origin;
const FALLBACK_ICONS_BASE_URL = "https://raw.githubusercontent.com/lobehub/lobe-icons/master/packages/static-svg/icons";

function fallbackIconUrl(url: string): string | undefined {
  // These SVG filenames are available in the upstream icon collection. Custom
  // PNGs and HeyGen have no matching upstream asset and retain their primary URL.
  return Object.values(brandAssets).includes(url) && url.endsWith(".svg") && !url.endsWith("/heygen.svg")
    ? `${FALLBACK_ICONS_BASE_URL}/${url.split("/").pop()}` : undefined;
}

async function fetchIconDataUrl(url: string): Promise<string> {
  const response = await fetch(url, { cache: "force-cache", signal: AbortSignal.timeout(5_000) });
  if (!response.ok) throw new Error(`icon request failed with ${response.status}`);
  if (!response.headers.get("content-type")?.startsWith("image/")) throw new Error("icon response is not an image");
  return blobToDataUrl(await response.blob());
}
const iconDataUrls = new Map<string, string>();
const iconFetches = new Map<string, Promise<void>>();
let iconStorageLoaded = false;

function loadIconStorage(): void {
  if (iconStorageLoaded) return;
  iconStorageLoaded = true;
  try {
    const raw = globalThis.localStorage?.getItem(ICON_CACHE_STORAGE_KEY);
    if (!raw) return;
    const entries: unknown = JSON.parse(raw);
    if (!entries || typeof entries !== "object") return;
    for (const [url, dataUrl] of Object.entries(entries)) {
      // Only adopt data: image entries; anything else in storage is ignored.
      if (typeof dataUrl === "string" && dataUrl.startsWith("data:image/")) {
        iconDataUrls.set(url, dataUrl);
      }
    }
  } catch {
    // A corrupted payload is equivalent to a cold cache.
  }
}

function persistIconStorage(): void {
  try {
    globalThis.localStorage?.setItem(ICON_CACHE_STORAGE_KEY, JSON.stringify(Object.fromEntries(iconDataUrls)));
  } catch {
    // Storage unavailable or full: keep the in-memory cache only.
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return blob.arrayBuffer().then((buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let offset = 0; offset < bytes.length; offset += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
    }
    return `data:${blob.type};base64,${btoa(binary)}`;
  });
}

export function getCachedModelIconSrc(url: string): string {
  loadIconStorage();
  return iconDataUrls.get(url) ?? url;
}

export function ensureModelIconCached(url: string): Promise<void> {
  loadIconStorage();
  const running = iconFetches.get(url);
  if (running) return running;
  if (iconDataUrls.has(url)) return Promise.resolve();
  let target: URL;
  try {
    target = new URL(url);
  } catch {
    return Promise.resolve();
  }
  // Only cache icons from the code-owned model-icons origin.
  if (target.origin !== ICON_CACHE_ORIGIN) return Promise.resolve();
  const task = fetchIconDataUrl(url)
    .catch(error => {
      const fallback = fallbackIconUrl(url);
      if (!fallback) throw error;
      return fetchIconDataUrl(fallback);
    })
    .then((dataUrl) => {
      iconDataUrls.set(url, dataUrl);
      persistIconStorage();
    })
    .catch(() => {
      // The image error handler displays a letter if both sources fail.
    })
    .finally(() => {
      iconFetches.delete(url);
    });
  iconFetches.set(url, task);
  return task;
}

export function resolveModelBrand(...sources: Array<string | null | undefined>): ModelBrand {
  const haystack = sources.filter(Boolean).join(" ").toLowerCase();
  if (!haystack) return "generic";
  for (const [brand, tokens] of brandOrder) {
    if (tokens.some((token) => haystack.includes(token))) return brand;
  }
  return "generic";
}

export function getModelIconSrc(...sources: Array<string | null | undefined>): string | undefined {
  const brand = resolveModelBrand(...sources);
  return brand === "generic" ? undefined : brandAssets[brand];
}

export function ModelIcon({
  source,
  className,
  size = "md",
}: Readonly<{
  source: string | ReadonlyArray<string | null | undefined>;
  className?: string;
  size?: keyof typeof sizeClass;
}>) {
  const sources = Array.isArray(source) ? source : [source];
  const brand = resolveModelBrand(...sources);
  const assetUrl = brand === "generic" ? undefined : brandAssets[brand];
  const [loaded, setLoaded] = useState<{ url: string; src?: string } | null>(null);
  useEffect(() => {
    if (!assetUrl) return;
    let cancelled = false;
    void ensureModelIconCached(assetUrl).then(() => {
      const cached = getCachedModelIconSrc(assetUrl);
      if (!cancelled && cached !== assetUrl) setLoaded({ url: assetUrl, src: cached });
    });
    return () => { cancelled = true; };
  }, [assetUrl]);
  const src = loaded?.url === assetUrl ? loaded?.src : assetUrl ? getCachedModelIconSrc(assetUrl) : undefined;
  const text = sources.find((item): item is string => Boolean(item && item.trim())) ?? "";
  const letter = (text.split("/").pop() ?? text).replace(/^[^a-z0-9]+/i, "").slice(0, 1).toUpperCase() || "M";
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden bg-white ring-1 ring-inset ring-zinc-200",
        sizeClass[size],
        className,
      )}
      title={brand === "generic" ? undefined : brand}
    >
      {src ? (
        <img alt="" className="size-[72%] object-contain" src={src} onError={() => { if (assetUrl) setLoaded({ url: assetUrl }); }} />
      ) : (
        <span className="text-xs font-bold leading-none text-zinc-500">{letter}</span>
      )}
    </span>
  );
}
